import React, { useEffect, useState } from "react";
import api from "../../../api";
import { IMAGE_URL } from "../../../path";
import { BlogCategory } from "../../../Blog";

interface BlogCategoryModalProps {
    category: BlogCategory | null;
    onClose: () => void;
    onSaved: () => void;
}

const BlogCategoryModal: React.FC<BlogCategoryModalProps> = ({
                                                                 category,
                                                                 onClose,
                                                                 onSaved,
                                                             }) => {
    const [name, setName] = useState("");
    const [icon, setIcon] = useState<File | null>(null);
    const [iconPreview, setIconPreview] = useState<string | null>(null);
    const [active, setActive] = useState(true);
    const [displayOrder, setDisplayOrder] = useState(0);

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (category) {
            setName(category.name);
            setActive(category.active);
            setDisplayOrder(category.display_order);

            if (category.icon) {
                setIconPreview(`${IMAGE_URL}/${category.icon}`);
            } else {
                setIconPreview(null);
            }
        } else {
            setName("");
            setIcon(null);
            setIconPreview(null);
            setActive(true);
            setDisplayOrder(0);
        }

        setError("");
    }, [category]);

    const handleIconChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0] || null;

        setIcon(file);

        if (file) {
            setIconPreview(URL.createObjectURL(file));
        } else {
            setIconPreview(null);
        }
    };

    const handleSave = async () => {
        if (!name.trim()) {
            setError("Please enter a category name.");
            return;
        }

        if (!category && !icon) {
            setError("Please select an icon.");
            return;
        }

        try {
            setSaving(true);
            setError("");

            const formData = new FormData();

            formData.append("name", name.trim());
            formData.append("active", active ? "1" : "0");
            formData.append(
                "display_order",
                displayOrder.toString()
            );

            if (icon) {
                formData.append("icon", icon);
            }

            if (category) {
                formData.append("_method", "PUT");

                await api.post(
                    `/admin/blog-categories/${category.id}`,
                    formData
                );
            } else {
                await api.post(
                    "/admin/blog-categories",
                    formData
                );
            }

            onSaved();
        } catch (err: any) {
            console.error(err);

            const validationErrors =
                err?.response?.data?.errors;

            if (validationErrors) {
                const firstError = Object.values(
                    validationErrors
                )[0] as string[];

                setError(firstError?.[0] || "Validation failed.");
            } else {
                setError(
                    err?.response?.data?.message ||
                    "Failed to save blog category."
                );
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="admin-modal-overlay">
            <div className="admin-modal">
                <div className="admin-modal-header">
                    <h2>
                        {category
                            ? "Edit Blog Category"
                            : "Add Blog Category"}
                    </h2>

                    <button
                        type="button"
                        className="admin-modal-close"
                        onClick={onClose}
                    >
                        ×
                    </button>
                </div>

                <div className="admin-modal-content">
                    {error && (
                        <div className="form-error">
                            {error}
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="blog-category-name">
                            Name
                        </label>

                        <input
                            id="blog-category-name"
                            type="text"
                            value={name}
                            onChange={(e) =>
                                setName(e.target.value)
                            }
                            placeholder="Enter category name"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="blog-category-icon">
                            Icon
                        </label>

                        <input
                            id="blog-category-icon"
                            type="file"
                            accept="image/*,.svg"
                            onChange={handleIconChange}
                        />

                        {iconPreview && (
                            <div className="admin-image-preview">
                                <img
                                    src={iconPreview}
                                    alt="Category icon preview"
                                    className="admin-svg-preview"
                                />
                            </div>
                        )}

                        <small>
                            SVG, JPG, PNG, GIF or WebP. Maximum 5MB.
                        </small>
                    </div>

                    <div className="form-group">
                        <label className="admin-checkbox-label">
                            <input
                                type="checkbox"
                                checked={active}
                                onChange={(e) =>
                                    setActive(e.target.checked)
                                }
                            />
                            Active
                        </label>
                    </div>

                    <div className="form-group">
                        <label htmlFor="blog-category-display-order">
                            Display Order
                        </label>

                        <input
                            id="blog-category-display-order"
                            type="number"
                            min="0"
                            value={displayOrder}
                            onChange={(e) =>
                                setDisplayOrder(
                                    Number(e.target.value)
                                )
                            }
                        />
                    </div>

                    <div className="admin-modal-actions">
                        <button
                            type="button"
                            className="admin-table-button"
                            onClick={onClose}
                            disabled={saving}
                        >
                            Cancel
                        </button>

                        <button
                            type="button"
                            className="admin-primary-button"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving
                                ? "Saving..."
                                : "Save Changes"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BlogCategoryModal;
